import { registerLocaleData } from '@angular/common';
import localeEsAr from '@angular/common/locales/es-AR';
import { provideHttpClient, withFetch, withInterceptors } from '@angular/common/http';
import {
  inject,
  ApplicationConfig,
  DestroyRef,
  LOCALE_ID,
  provideAppInitializer,
  provideBrowserGlobalErrorListeners,
  provideZoneChangeDetection,
} from '@angular/core';
import { provideAnimationsAsync } from '@angular/platform-browser/animations/async';
import { provideRouter, withComponentInputBinding, withInMemoryScrolling } from '@angular/router';
import { MAT_FORM_FIELD_DEFAULT_OPTIONS } from '@angular/material/form-field';
import { MatIconRegistry } from '@angular/material/icon';

import { EventosAbiertosStore } from '@compartido/datos/eventos-abiertos.store';
import { PendientesMantenimientoStore } from '@compartido/mantenimiento/pendientes-mantenimiento.store';
import { apiUrlInterceptor } from '@core/http/api-url.interceptor';
import { authInterceptor } from '@core/http/auth.interceptor';
import { erroresInterceptor } from '@core/http/errores.interceptor';
import { loadingInterceptor } from '@core/http/loading.interceptor';
import { OrigenService } from '@core/navegacion/origen.service';
import { BusquedaGlobalService } from '@compartido/busqueda/busqueda-global.service';
import { BUSCADOR_GLOBAL } from '@core/ui/buscador-global';
import { CONTADORES_MENU, type ContadoresMenu } from '@core/ui/contadores-menu';
import { ENCUADRE_INICIAL_MAPA } from '@core/ui/encuadre-inicial-mapa';
import { VersionService } from '@core/version/version.service';
import { encuadreDelCliente } from '@compartido/clientes/encuadre-cliente';
import { CREAR_PERFIL, type CrearPerfil } from '@compartido/perfiles/crear-perfil';
import { firstValueFrom } from 'rxjs';
import { routes } from './app.routes';

registerLocaleData(localeEsAr);

/**
 * Los badges del menú: Eventos, y en Mantenimiento las solicitudes y los
 * pedidos de materiales pendientes (la sección muestra la suma, como IRIX).
 *
 * Se cablea acá porque es el único lugar que puede conocer a la vez al shell
 * (`ui/`) y al dominio: el token vive en `core/` y la implementación en
 * `compartido/`.
 */
function contadoresDelMenu(): ContadoresMenu {
  const mantenimiento = inject(PendientesMantenimientoStore);
  return {
    '/eventos': inject(EventosAbiertosStore).abiertos,
    '/mantenimiento/solicitudes': mantenimiento.solicitudes,
    '/mantenimiento/materiales': mantenimiento.materiales,
  };
}

// El formulario de perfil es de Operación; se carga recién cuando otro dominio
// (p. ej. el formulario de cliente) pide crear uno.
const crearPerfil: CrearPerfil = async (dialog, opciones) => {
  const { abrirFormularioPerfil } =
    await import('./dominio/operacion/perfiles/formulario-perfil.component');
  return firstValueFrom(abrirFormularioPerfil(dialog, opciones).afterClosed());
};

export const appConfig: ApplicationConfig = {
  providers: [
    { provide: CONTADORES_MENU, useFactory: contadoresDelMenu },
    // El buscador del encabezado vive en `ui/` y no puede conocer entidades de
    // negocio: acá se le pasa quién sabe buscarlas.
    { provide: BUSCADOR_GLOBAL, useExisting: BusquedaGlobalService },
    { provide: CREAR_PERFIL, useValue: crearPerfil },
    // Igual que el buscador: el mapa (`ui/`) arranca sobre el polígono del
    // cliente sin conocer `ICliente`.
    { provide: ENCUADRE_INICIAL_MAPA, useFactory: encuadreDelCliente },
    provideBrowserGlobalErrorListeners(),

    /**
     * Se mantiene `zone.js`.
     *
     * Todo lo que se escribe en FARO es signals-first, así que zoneless sería
     * viable — pero las fases 2 y 3 copian unas 100.000 líneas de IRIX escritas
     * para detección de cambios por zona. Cuando ese código esté migrado, esto se
     * cambia por `provideZonelessChangeDetection()` y se saca el polyfill.
     */
    provideZoneChangeDetection({ eventCoalescing: true }),

    provideRouter(
      routes,
      // Los parámetros de ruta entran como `input()` del componente.
      withComponentInputBinding(),
      withInMemoryScrolling({ scrollPositionRestoration: 'enabled', anchorScrolling: 'enabled' }),
    ),

    /**
     * El orden importa: la URL se completa primero, después se autentica, y el
     * manejo de errores queda por fuera para ver también los fallos del refresco.
     */
    provideHttpClient(
      withFetch(),
      withInterceptors([
        loadingInterceptor,
        erroresInterceptor,
        authInterceptor,
        apiUrlInterceptor,
      ]),
    ),

    provideAnimationsAsync(),

    // De dónde vino cada página (migas y flecha de volver): tiene que escuchar
    // desde la primera navegación.
    provideAppInitializer(() => inject(OrigenService).iniciar(inject(DestroyRef))),

    // La versión publicada (`assets/env.json`) y el aviso de una más nueva. No
    // se espera: si el archivo tarda, la app arranca igual con `VERSION`.
    provideAppInitializer(() => {
      void inject(VersionService).iniciar(inject(DestroyRef));
    }),

    { provide: LOCALE_ID, useValue: 'es-AR' },
    { provide: MAT_FORM_FIELD_DEFAULT_OPTIONS, useValue: { appearance: 'outline' } },

    /**
     * Íconos en trazo lineal, como pide el manual de marca (07 · Iconografía).
     * Material Symbols Outlined es la única fuente: tiene todos los nombres de
     * Material Icons y los que Material Icons no trae (`table_lamp`,
     * `light_off`…). Antes eran dos, y un nombre que no existía en la que le
     * tocaba se veía como texto. Qué ícono usar para cada cosa: `docs/ICONOS.md`.
     */
    provideAppInitializer(() => {
      inject(MatIconRegistry).setDefaultFontSetClass(
        'material-symbols-outlined',
        'mat-ligature-font',
      );
    }),
  ],
};
